import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { JwtInterceptor } from './helpers/jwt.interceptor';
import { NgxPaginationModule } from 'ngx-pagination';
import { AppRoutingModule } from './app-routing.module';

import { AuthService } from "./services/auth";

import { AppComponent } from './app.component';
import { TopbarComponent } from './components/topbar/topbar.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { ErrorComponent } from './components/error/error.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { LoginComponent } from './components/login/login.component';
import { ChecklistGeneralComponent } from './components/checklist/checklist-general/checklist-general.component';
import { ChecklistCreateComponent } from './components/checklist/checklist-create/checklist-create.component';
import { ChecklistUpdateComponent } from './components/checklist/checklist-update/checklist-update.component';
import { ChecklistDetailComponent } from './components/checklist/checklist-detail/checklist-detail.component';
import { ChecklistCategoryDetailComponent } from './components/checklist-category/checklist-category-detail/checklist-category-detail.component';
import { ChecklistCategoryCreateComponent } from './components/checklist-category/checklist-category-create/checklist-category-create.component';
import { ChecklistCategoryUpdateComponent } from './components/checklist-category/checklist-category-update/checklist-category-update.component';
import { ChecklistSubcategoryCreateComponent } from './components/checklist-subcategory/checklist-subcategory-create/checklist-subcategory-create.component';
import { ChecklistSubcategoryUpdateComponent } from './components/checklist-subcategory/checklist-subcategory-update/checklist-subcategory-update.component';
import { ContractGeneralComponent } from './components/contract/contract-general/contract-general.component';
import { ContractCreateComponent } from './components/contract/contract-create/contract-create.component';
import { ContractUpdateComponent } from './components/contract/contract-update/contract-update.component';
import { ContractDetailComponent } from './components/contract/contract-detail/contract-detail.component';
import { InspectionImportComponent } from './components/inspection/inspection-import/inspection-import.component';
import { InspectionDetailComponent } from './components/inspection/inspection-detail/inspection-detail.component';
import { InspectionOrderDetailComponent } from './components/inspection-order/inspection-order-detail/inspection-order-detail.component';
import { InspectionGeneralComponent } from './components/inspection/inspection-general/inspection-general.component';
import { UserCreateComponent } from './components/user/user-create/user-create.component';
import { UserUpdateComponent } from './components/user/user-update/user-update.component';
import { UserDeactiveModalComponent } from './modals/user/user-deactive-modal/user-deactive-modal.component';
import { UserActiveModalComponent } from './modals/user/user-active-modal/user-active-modal.component';
import { UserDeleteModalComponent } from './modals/user/user-delete-modal/user-delete-modal.component';
import { UserProfileComponent } from './components/user/user-profile/user-profile.component';

import { ChecklistFilterPipe } from './pipes/checklist/checklist-filter.pipe';
import { ChecklistTitleFilterPipe } from './pipes/checklist/checklist-title-filter.pipe';
import { ChecklistDescriptionFilterPipe } from './pipes/checklist/checklist-description-filter.pipe';
import { ContractFilterPipe } from './pipes/contract/contract-filter.pipe';
import { ContractTitleFilterPipe } from './pipes/contract/contract-title-filter.pipe';
import { ContractCodeFilterPipe } from './pipes/contract/contract-code-filter.pipe';
import { UsernameFilterPipe } from './pipes/user/username-filter.pipe';
import { RoleFilterPipe } from './pipes/user/role-filter.pipe';
import { ActiveFilterPipe } from './pipes/user/active-filter.pipe';
import { UserFilterPipe } from './pipes/user/user-filter.pipe';
import { InspectionInspectorFilterPipe } from './pipes/inspection/inspection-inspector-filter.pipe';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { UserGeneralComponent } from "./components/user/user-general/user-general.component";
import { GeneralModalComponent } from './modals/general/general-modal/general-modal.component';
import { ContractFounderCreateModalComponent } from "./modals/contract/contract-founder-create-modal.component";
import { ReservationCreateModalComponent } from './modals/reservation/reservation-create-modal/reservation-create-modal.component';
import { InspectionAssignModalComponent } from './modals/inspection/inspection-assign-modal/inspection-assign-modal.component';
import { InspectionViewReservationModalComponent } from './modals/inspection/inspection-view-reservation-modal/inspection-view-reservation-modal.component';
import { InspectionChecklistComponent } from './components/inspection/inspection-checklist/inspection-checklist.component';
import { InspectionCreateComponent } from './components/inspection/inspection-create/inspection-create.component';
import { InspectionUpdateComponent } from './components/inspection/inspection-update/inspection-update.component';
import { InspectionOrderCreateComponent } from './components/inspection-order/inspection-order-create/inspection-order-create.component';
import { InspectionOrderUpdateComponent } from './components/inspection-order/inspection-order-update/inspection-order-update.component';
import { InspectionDeliveryRequestCreateComponent } from './components/inspection-delivery-request/inspection-delivery-request-create/inspection-delivery-request-create.component';
import { InspectionDeliveryRequestUpdateComponent } from './components/inspection-delivery-request/inspection-delivery-request-update/inspection-delivery-request-update.component';
import { InspectionDocumentCreateComponent } from './components/inspection-document/inspection-document-create/inspection-document-create.component';
import { ContractReportComponent } from './components/contract/contract-report/contract-report.component';
import { InspectionDocumentUploadComponent } from './modals/inspection/inspection-document-upload/inspection-document-upload.component';

@NgModule({
  declarations: [
    AppComponent,
    TopbarComponent,
    SidebarComponent,
    ErrorComponent,
    DashboardComponent,
    LoginComponent,
    ChecklistCreateComponent,
    ChecklistGeneralComponent,
    ChecklistUpdateComponent,
    ChecklistDetailComponent,
    ChecklistCategoryDetailComponent,
    ChecklistCategoryCreateComponent,
    ChecklistCategoryUpdateComponent,
    ChecklistSubcategoryCreateComponent,
    ChecklistSubcategoryUpdateComponent,
    ContractGeneralComponent,
    ContractCreateComponent,
    ContractUpdateComponent,
    ContractDetailComponent,
    ChecklistFilterPipe,
    ChecklistTitleFilterPipe,
    ChecklistDescriptionFilterPipe,
    ContractFilterPipe,
    ContractTitleFilterPipe,
    ContractCodeFilterPipe,
    InspectionImportComponent,
    InspectionDetailComponent,
    InspectionOrderDetailComponent,
    InspectionGeneralComponent,
    UsernameFilterPipe,
    RoleFilterPipe,
    ActiveFilterPipe,
    UserFilterPipe,
    InspectionInspectorFilterPipe,
    UserCreateComponent,
    UserUpdateComponent,
    UserGeneralComponent,
    UserDeleteModalComponent,
    UserDeactiveModalComponent,
    UserActiveModalComponent,
    ContractFounderCreateModalComponent,
    UserProfileComponent,
    GeneralModalComponent,
    ReservationCreateModalComponent,
    InspectionAssignModalComponent,
    InspectionViewReservationModalComponent,
    InspectionChecklistComponent,
    InspectionCreateComponent,
    InspectionUpdateComponent,
    InspectionOrderCreateComponent,
    InspectionOrderUpdateComponent,
    InspectionDeliveryRequestCreateComponent,
    InspectionDeliveryRequestUpdateComponent,
    InspectionDocumentCreateComponent,
    ContractReportComponent,
    InspectionDocumentUploadComponent
  ],
  imports: [
    BrowserModule,
    FormsModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatRadioModule,
    HttpClientModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    NgxPaginationModule,
    NgbModule
  ],
  entryComponents: [
    UserActiveModalComponent,
    UserDeactiveModalComponent,
    UserDeleteModalComponent,
    ContractFounderCreateModalComponent
  ],
  providers: [
    AuthService,
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: MAT_DATE_LOCALE, useValue: 'it-IT' }
  ],

  bootstrap: [AppComponent]
})
export class AppModule { }
